import React, { useState, useEffect } from "react";
import { staffAPI } from "../../services/api";
import { handleApiError } from "../../utils/errorUtils";
import { toast } from "react-toastify";
import {
  FaCreditCard,
  FaSpinner,
  FaExternalLinkAlt,
  FaCheckCircle,
  FaExclamationTriangle,
} from "react-icons/fa";
import "./CustomerPaymentManagement.css";

/**
 * CustomerPaymentManagement component for managing customer payment methods
 * Uses Stripe Customer Portal for secure payment method management
 */
const CustomerPaymentManagement = ({ customerId, customer }) => {
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  // Fetch payment method status on component mount
  useEffect(() => {
    fetchPaymentStatus();
  }, [customerId]);

  // Fetch customer payment method status from Stripe
  const fetchPaymentStatus = async () => {
    try {
      setLoading(true);
      const response = await staffAPI.getCustomerPaymentStatus(customerId);
      setPaymentStatus(response.data);
    } catch (error) {
      handleApiError(
        error,
        "CustomerPaymentManagement.fetchPaymentStatus",
        (message) => toast.error(message),
        "Error fetching payment method status"
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle opening Stripe Customer Portal
  const handleOpenCustomerPortal = async () => {
    setProcessing(true);

    try {
      const response = await staffAPI.createCustomerPortalSession(customerId);

      // Open Stripe Customer Portal in new tab
      window.open(response.data.url, "_blank", "noopener,noreferrer");

      toast.success("Customer billing portal opened successfully");

      // Refresh payment status after a short delay
      setTimeout(() => {
        fetchPaymentStatus();
      }, 2000);
    } catch (error) {
      handleApiError(
        error,
        "CustomerPaymentManagement.openCustomerPortal",
        (message) => toast.error(message),
        "Error opening customer billing portal"
      );
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="payment-management-loading">
        <FaSpinner className="spinning" />
        <p>Loading payment method status...</p>
      </div>
    );
  }

  return (
    <div className="customer-payment-management">
      <div className="payment-header">
        <h3>Payment Methods</h3>
        <div className="payment-status">
          {paymentStatus?.hasPaymentMethods ? (
            <div className="status-indicator success">
              <FaCheckCircle />
              <span>Payment methods stored in Stripe</span>
              <small>
                ({paymentStatus.paymentMethodCount} method
                {paymentStatus.paymentMethodCount !== 1 ? "s" : ""})
              </small>
            </div>
          ) : (
            <div className="status-indicator warning">
              <FaExclamationTriangle />
              <span>No payment methods on file</span>
            </div>
          )}
        </div>
      </div>

      <div className="payment-management-content">
        <div className="stripe-portal-section">
          <div className="portal-info">
            <FaCreditCard size={48} />
            <h4>Secure Payment Management</h4>
            <p>
              All payment methods are securely stored and managed through
              Stripe's Customer Portal. This ensures the highest level of
              security and compliance for sensitive payment information.
            </p>

            <div className="portal-features">
              <ul>
                <li>Add new payment methods</li>
                <li>Update existing cards</li>
                <li>Set default payment method</li>
                <li>Remove old payment methods</li>
                <li>View billing history</li>
                <li>Download invoices</li>
              </ul>
            </div>
          </div>

          <div className="portal-actions">
            <button
              className="manage-billing-btn"
              onClick={handleOpenCustomerPortal}
              disabled={processing || !paymentStatus?.stripeCustomerId}
            >
              {processing ? (
                <>
                  <FaSpinner className="spinning" />
                  Opening Portal...
                </>
              ) : (
                <>
                  <FaExternalLinkAlt />
                  Manage Customer Billing
                </>
              )}
            </button>

            {!paymentStatus?.stripeCustomerId && (
              <p className="no-stripe-customer">
                Customer has no Stripe customer ID. Cannot access billing
                portal.
              </p>
            )}
          </div>
        </div>

        <div className="security-notice">
          <h5>Security & Compliance</h5>
          <p>
            By using Stripe's Customer Portal, all payment data remains secure
            and PCI-compliant. No sensitive payment information is stored or
            transmitted through our application.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CustomerPaymentManagement;
